'use client'

import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'

import { AuthGatedAction, directoryLoginHref } from '@/components/directory/auth-gated-action'
import { useAuth } from '@/contexts/AuthContext'
import { trackEvent } from '@/lib/analytics'

/**
 * The saved-only pill at the foot of the filter rail.
 *
 * The rest of the rail is a plain GET form with no JavaScript, and this control
 * cannot be: whether a viewer is signed in is a client fact on an ISR page, and
 * the two answers lead to different destinations. Signed in it is a link that
 * flips the `saved` key on the current URL, so the filtered view is still an
 * address a reader can bookmark or share. Signed out the destination is the
 * sign-in wall, which is a button rather than an anchor.
 */

function withoutPage(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params)
  next.delete('page')
  return next
}

export function SavedOnlyToggle({ surface }: { surface: string }) {
  const { user } = useAuth()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const params = withoutPage(new URLSearchParams(searchParams?.toString() ?? ''))
  const on = params.get('saved') === '1'

  const withSaved = new URLSearchParams(params)
  withSaved.set('saved', '1')
  const withoutSaved = new URLSearchParams(params)
  withoutSaved.delete('saved')

  const query = (search: URLSearchParams) => {
    const qs = search.toString()
    return qs ? `${pathname}?${qs}` : pathname
  }

  const target = on ? query(withoutSaved) : query(withSaved)
  const className =
    'mt-6 flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 px-3 text-sm font-semibold text-gray-900 transition-colors hover:border-green-300 hover:text-green-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600'
  const track = () => trackEvent('directory_list_saved_toggled', { surface, on: !on })

  const body = (
    <>
      <span>Saved only</span>
      <span className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500">{on ? 'On' : 'Off'}</span>
        <span
          aria-hidden="true"
          className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
            on ? 'bg-green-700' : 'bg-gray-300'
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
              on ? 'left-4.5' : 'left-0.5'
            }`}
          />
        </span>
      </span>
    </>
  )

  if (!user) {
    return (
      <AuthGatedAction
        href={directoryLoginHref(query(withSaved))}
        role="switch"
        aria-checked={on}
        onNavigate={track}
        className={className}
      >
        {body}
      </AuthGatedAction>
    )
  }

  return (
    <Link
      href={target}
      prefetch={false}
      role="switch"
      aria-checked={on}
      onClick={track}
      className={className}
    >
      {body}
    </Link>
  )
}

export default SavedOnlyToggle
